import React, { useState, useEffect } from 'react';
import { useNavigate, useResolvedPath } from 'react-router-dom';
import { notification } from 'antd';

import { Modal, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { LoadingOutlined, ReloadOutlined, DeleteOutlined, PlusOutlined, QuestionCircleOutlined, EditOutlined } from '@ant-design/icons';

import Toolbar from '@controls/toolbar/toolbar';

import { exception, securityRestriction } from '@extensions/notification';
import { Permission, hasPermission } from '@enums/permission';

import { userLoaded } from '@store/actions';
import { useAppSelector, useAppDispatch } from '@store/hooks';

import { IUserSession } from '@entities/user-session';
import { IWarehouse } from '@entities/warehouse';

import { WarehouseType, enumLabel } from '@enums/warehouse-type';

import { serverFetch } from '@flc/server';

const Warehouses = () => {
    const navigate = useNavigate();
    const resolved = useResolvedPath('');
    const [api, contextHolder] = notification.useNotification();
    const [modal, modalContextHolder] = Modal.useModal();

    const d = useAppDispatch();
    const userSession = useAppSelector<IUserSession>((s) => s.userSession);

    const [warehouses, setWarehouses] = useState<Array<IWarehouse>>([]);
    const [currentWarehouse, setCurrentWarehouse] = useState<IWarehouse>();

    const [selectedIds, setSelectedIds] = useState<React.Key[]>([]);
    const [refreshRequired, setRefreshRequired] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        if (!refreshRequired) return;

        setRefreshRequired(false);
        init();
    }, [refreshRequired]);

    const init = () => {
        setWarehouses([]);

        setLoading(true);

        serverFetch('warehouses', { method: 'GET' })
            .then((data) => {
                setWarehouses(data);
                setLoading(false);
            })
            .catch((ex) => {
                exception(api, 'Ошибка получения складов', ex, () => d(userLoaded(undefined)));
                setLoading(false);
            });
    };

    const onDelete = () => {
        if (!currentWarehouse) return;

        serverFetch(`warehouses/${currentWarehouse.id}`, { method: 'DELETE' })
            .then(() => {
                onSelectChange([]);
                setRefreshRequired(true);
            })
            .catch((ex) => {
                exception(api, 'Ошибка удаления склада', ex, () => d(userLoaded(undefined)));
            });
    };

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        key: 'refresh',
                        disabled: loading,
                        icon: <ReloadOutlined />,
                        onClick: () => {
                            setRefreshRequired(true);
                        },
                    },
                    {
                        type: 'primary',
                        label: 'Добавить',
                        key: 'add',
                        icon: <PlusOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageWarehouses)) {
                                securityRestriction(api, [Permission.ManageWarehouses]);
                                return;
                            }

                            navigate(`${resolved.pathname}/new`);
                        },
                    },
                    {
                        label: 'Изменить',
                        key: 'edit',
                        disabled: !currentWarehouse,
                        icon: <EditOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageWarehouses)) {
                                securityRestriction(api, [Permission.ManageWarehouses]);
                                return;
                            }

                            navigate(`${resolved.pathname}/${currentWarehouse?.id}`);
                        },
                    },
                    {
                        label: 'Удалить',
                        key: 'delete',
                        disabled: !currentWarehouse,
                        icon: <DeleteOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.DeleteWarehouse)) {
                                securityRestriction(api, [Permission.DeleteWarehouse]);
                                return;
                            }

                            modal.confirm({
                                title: `Удалить склад "${currentWarehouse?.code}"?`,
                                icon: <QuestionCircleOutlined />,
                                okType: 'primary',
                                okText: 'Удалить',
                                cancelText: 'Отмена',
                                onOk: () => {
                                    onDelete();
                                },
                            });
                        },
                    },
                ]}
            />
        );
    };

    const onSelectChange = (selectedRowKeys: React.Key[]) => {
        setSelectedIds(selectedRowKeys);

        if (selectedRowKeys.length == 1) {
            const entity = warehouses.find((o) => o.id == selectedRowKeys[0]);
            setCurrentWarehouse(entity);
        } else {
            setCurrentWarehouse(undefined);
        }
    };

    const renderTable = () => {
        const columns: ColumnsType<IWarehouse> = [
            {
                title: 'Город доставки',
                dataIndex: 'cityName',
                width: 200,
                onCell: (record) => ({
                    style: {
                        fontWeight: record.type === WarehouseType.Main ? 600 : '',
                    },
                }),
            },
            {
                title: 'Код',
                dataIndex: 'code',
                width: 200,
                onCell: (record) => ({
                    style: {
                        fontWeight: record.type === WarehouseType.Main ? 600 : '',
                    },
                }),
            },
            {
                title: 'Тип',
                width: 120,
                onCell: (record) => ({
                    style: {
                        fontWeight: record.type === WarehouseType.Main ? 600 : '',
                    },
                }),
                render: (_: any, record: IWarehouse) => {
                    return enumLabel(record.type);
                },
            },
            {
                title: 'Адрес',
                dataIndex: 'address',
                width: 500,
                onCell: (record) => ({
                    style: {
                        fontWeight: record.type === WarehouseType.Main ? 600 : '',
                    },
                }),
            },
            {},
        ];

        const tableLoading = {
            spinning: loading,
            indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
        };

        return (
            <Table
                rowKey='id'
                size='small'
                rowSelection={{
                    selectedRowKeys: selectedIds,
                    onChange: onSelectChange,
                    type: 'radio',
                }}
                columns={columns}
                dataSource={warehouses}
                loading={tableLoading}
                onRow={(record: IWarehouse) => {
                    return {
                        onClick: (event) => {
                            if (!record.id) return;

                            onSelectChange([record.id]);
                        },
                    };
                }}
                pagination={false}
                scroll={{ y: `calc(100vh - 175px)` }}
            />
        );
    };

    return (
        <>
            {renderToolbar()}
            {renderTable()}

            {contextHolder}
            {modalContextHolder}
        </>
    );
};

export default Warehouses;
